import React, { useState, useRef, useEffect } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  useToast,
  Card,
  CardBody,
  Heading,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Progress,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  IconButton,
  Spinner,
  Checkbox,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Select,
} from '@chakra-ui/react'
import { AddIcon, AttachmentIcon, DownloadIcon, DeleteIcon, ExternalLinkIcon, ViewIcon, ChevronLeftIcon, ChevronRightIcon, RepeatIcon } from '@chakra-ui/icons'
import axios from 'axios'
import { hasPermission, type Role } from '../config/permissions'

interface FileInfo {
  filename: string
  originalName: string
  size: number
  uploadDate: string
  formattedDate?: string
  vectorStatus?: 'completed' | 'pending' | 'error' | 'unknown'
  vectorCount?: number
}

interface VectorDocument {
  id: string
  filename: string
  content_preview: string
  full_content: string
  metadata: any
}

interface CrawlResult {
  url: string
  title: string
  chunks_count: number
  content_length: number
}

const FileManagementPage: React.FC = () => {
  const toast = useToast()
  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  
  // 권한 확인
  const userRole = (localStorage.getItem('userRole') || 'admin') as Role
  const username = localStorage.getItem('username') || ''
  const canUpload = hasPermission(userRole, 'FILES_UPLOAD')
  const isAdmin = userRole === 'admin'  // admin 권한 확인 (dmstudio 등)
  
  // 파일 업로드 상태
  const [files, setFiles] = useState<FileInfo[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 벡터 문서 상태
  const [vectorDocuments, setVectorDocuments] = useState<VectorDocument[]>([])
  const [isLoadingVectors, setIsLoadingVectors] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [pageSize, setPageSize] = useState(10) // 페이지당 항목 수
  
  // TensorFlow Projector 다운로드 상태
  const [isDownloadingProjector, setIsDownloadingProjector] = useState(false)
  
  // CSV 다운로드 상태
  const [isDownloadingCSV, setIsDownloadingCSV] = useState(false)
  
  // 다중 선택 상태
  const [selectedChunks, setSelectedChunks] = useState<Set<string>>(new Set())
  const [isDeletingMultiple, setIsDeletingMultiple] = useState(false)

  // 웹 크롤링 상태
  const [crawlUrl, setCrawlUrl] = useState('')
  const [crawlSelector, setCrawlSelector] = useState('')
  const [isCrawling, setIsCrawling] = useState(false)
  const [crawlResults, setCrawlResults] = useState<CrawlResult[]>([])

  // 모달 상태
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isDeleteDialogOpen, onOpen: onDeleteDialogOpen, onClose: onDeleteDialogClose } = useDisclosure()
  const [selectedDocument, setSelectedDocument] = useState<VectorDocument | null>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)

  // 파일 목록 조회
  const fetchFiles = async () => {
    try {
      const response = await axios.get('/graph/files')
      if (response.data.success) {
        setFiles(response.data.data)
      }
    } catch (error) {
      console.error('파일 목록 조회 오류:', error)
    }
  }

  // 벡터 문서 목록 조회
  const fetchVectorDocuments = async (page = 1) => {
    try {
      setIsLoadingVectors(true)
      const response = await axios.get(`/graph/vectors?page=${page}&limit=${pageSize}`)
      if (response.data.success) {
        setVectorDocuments(response.data.data)
        setCurrentPage(response.data.pagination.page)
        setTotalPages(response.data.pagination.totalPages)
        setTotalItems(response.data.pagination.totalItems)
      }
    } catch (error) {
      console.error('벡터 문서 조회 오류:', error)
      toast({
        title: '오류',
        description: '벡터 문서를 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsLoadingVectors(false)
    }
  }

  useEffect(() => {
    fetchFiles()
    fetchVectorDocuments()
  }, [])

  // 🆕 v10.7: pageSize 변경 시 데이터 새로고침
  useEffect(() => {
    fetchVectorDocuments(1)
  }, [pageSize])

  // 페이지 번호 클릭 핸들러
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    fetchVectorDocuments(page)
  }

  // 🆕 v10.7: 페이지당 표시 개수 변경 핸들러
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize)
    setCurrentPage(1) // 페이지 크기 변경 시 첫 페이지로
  }

  // 표시할 페이지 번호 계산 (최대 5개 표시)
  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxPagesToShow = 5
    
    if (totalPages <= maxPagesToShow) {
      // 전체 페이지가 5개 이하면 모두 표시
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // 현재 페이지를 중심으로 앞뒤 페이지 계산
      let startPage = Math.max(1, currentPage - 2)
      let endPage = Math.min(totalPages, currentPage + 2)
      
      // 시작이 1이 아니면 첫 페이지와 생략 표시
      if (startPage > 1) {
        pages.push(1)
        if (startPage > 2) {
          pages.push('...')
        }
      }
      
      // 중간 페이지들 (최대 5개)
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i)
      }
      
      // 끝이 마지막 페이지가 아니면 생략 표시와 마지막 페이지
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...')
        }
        pages.push(totalPages)
      }
    }
    
    return pages
  }

  // 파일 업로드
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // 파일 크기 체크 (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: '파일 크기 초과',
        description: '파일 크기는 10MB를 초과할 수 없습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    // 파일 형식 체크
    const allowedTypes = ['.pdf', '.docx', '.txt', '.csv', '.json', '.md']
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
    if (!allowedTypes.includes(ext)) {
      toast({
        title: '지원하지 않는 파일 형식',
        description: 'PDF, DOCX, TXT, CSV, JSON, MD 파일만 업로드 가능합니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setIsUploading(true)
    setUploadProgress(0)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await axios.post('/graph/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0
          setUploadProgress(percentCompleted)
        },
      })

      if (response.data.success) {
        toast({
          title: '업로드 성공',
          description: '파일이 성공적으로 업로드되었습니다.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        fetchFiles()
        fetchVectorDocuments()
      }
    } catch (error) {
      console.error('파일 업로드 오류:', error)
      toast({
        title: '업로드 실패',
        description: '파일 업로드 중 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  // 파일 삭제
  const handleFileDelete = async (filename: string) => {
    if (!window.confirm('이 파일을 삭제하시겠습니까?')) return

    try {
      const response = await axios.delete(`/graph/files/${encodeURIComponent(filename)}`)
      if (response.data.success) {
        // 즉시 UI에서 파일 제거
        setFiles(prevFiles => prevFiles.filter(f => f.filename !== filename))
        
        // 삭제 성공 토스트 표시
        toast({
          title: '삭제 완료',
          description: '파일이 성공적으로 삭제되었습니다.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        
        // 백그라운드에서 목록 새로고침
        fetchFiles()
        fetchVectorDocuments()
      }
    } catch (error) {
      console.error('파일 삭제 오류:', error)
      toast({
        title: '삭제 실패',
        description: '파일 삭제 중 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  // 청크 삭제
  const handleChunkDelete = async (id: string) => {
    if (!window.confirm('이 청크를 삭제하시겠습니까?')) return

    try {
      const response = await axios.delete(`/graph/vectors/${encodeURIComponent(id)}`)
      if (response.data.success) {
        // 즉시 UI에서 청크 제거
        setVectorDocuments(prevDocs => prevDocs.filter(doc => doc.id !== id))
        
        // 삭제 성공 토스트 표시
        toast({
          title: '삭제 완료',
          description: '청크가 성공적으로 삭제되었습니다.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        
        // 백그라운드에서 목록 새로고침
        fetchVectorDocuments(currentPage)
      }
    } catch (error) {
      console.error('청크 삭제 오류:', error)
      toast({
        title: '삭제 실패',
        description: '청크 삭제 중 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  // 체크박스 선택/해제
  const handleCheckboxChange = (id: string) => {
    setSelectedChunks(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  // 전체 선택/해제
  const handleSelectAll = () => {
    if (selectedChunks.size === vectorDocuments.length) {
      setSelectedChunks(new Set())
    } else {
      setSelectedChunks(new Set(vectorDocuments.map(doc => doc.id)))
    }
  }

  // 다중 삭제
  const handleMultipleDelete = async () => {
    if (selectedChunks.size === 0) {
      toast({
        title: '선택 필요',
        description: '삭제할 청크를 선택해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    onDeleteDialogOpen()
  }

  // 다중 삭제 확인
  const confirmMultipleDelete = async () => {
    setIsDeletingMultiple(true)
    onDeleteDialogClose()

    try {
      const deletePromises = Array.from(selectedChunks).map(id =>
        axios.delete(`/graph/vectors/${encodeURIComponent(id)}`)
      )

      const results = await Promise.allSettled(deletePromises)
      
      const successCount = results.filter(r => r.status === 'fulfilled').length
      const failCount = results.filter(r => r.status === 'rejected').length

      // UI에서 선택된 청크 제거
      setVectorDocuments(prevDocs => 
        prevDocs.filter(doc => !selectedChunks.has(doc.id))
      )
      
      // 선택 초기화
      setSelectedChunks(new Set())

      // 결과 토스트
      if (failCount === 0) {
        toast({
          title: '삭제 완료',
          description: `${successCount}개의 청크가 성공적으로 삭제되었습니다.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
      } else {
        toast({
          title: '일부 삭제 실패',
          description: `${successCount}개 성공, ${failCount}개 실패`,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        })
      }

      // 목록 새로고침
      fetchVectorDocuments(currentPage)
    } catch (error) {
      console.error('다중 삭제 오류:', error)
      toast({
        title: '삭제 실패',
        description: '청크 삭제 중 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsDeletingMultiple(false)
    }
  }

  // 웹 크롤링
  const handleWebCrawl = async () => {
    if (!crawlUrl) {
      toast({
        title: '오류',
        description: 'URL을 입력해주세요.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setIsCrawling(true)

    try {
      const response = await axios.post('/graph/crawl', { 
        url: crawlUrl,
        selector: crawlSelector || undefined  // 비어있으면 undefined로 전송
      })
      if (response.data.success) {
        toast({
          title: '크롤링 성공',
          description: `${response.data.data.chunks_count}개 청크가 벡터화되었습니다.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        setCrawlResults([response.data.data, ...crawlResults])
        setCrawlUrl('')
        setCrawlSelector('')
        fetchVectorDocuments()
      }
    } catch (error: any) {
      console.error('웹 크롤링 오류:', error)
      toast({
        title: '크롤링 실패',
        description: error.response?.data?.message || '웹 크롤링 중 오류가 발생했습니다.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsCrawling(false)
    }
  }

  // 청크 상세보기
  const handleViewChunk = (doc: VectorDocument) => {
    setSelectedDocument(doc)
    onOpen()
  }

  // TensorFlow Projector ZIP 다운로드
  const handleDownloadProjector = async () => {
    setIsDownloadingProjector(true)
    
    try {
      // 다운로드 링크를 생성하여 클릭
      const downloadUrl = `/graph/projector/download-all?limit=1000`
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = 'projector_data.zip'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      toast({
        title: '다운로드 시작',
        description: 'TensorFlow Projector 데이터를 다운로드 중입니다...',
        status: 'info',
        duration: 3000,
        isClosable: true,
      })
      
      // 다운로드 후 3초 뒤에 로딩 상태 해제
      setTimeout(() => {
        setIsDownloadingProjector(false)
      }, 3000)
      
    } catch (error) {
      console.error('Projector 다운로드 오류:', error)
      toast({
        title: '다운로드 실패',
        description: 'TensorFlow Projector 데이터 다운로드 중 오류가 발생했습니다.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      setIsDownloadingProjector(false)
    }
  }

  // CSV 다운로드
  const handleDownloadCSV = async () => {
    setIsDownloadingCSV(true)
    
    try {
      // 다운로드 링크를 생성하여 클릭
      const downloadUrl = `/graph/vectors/export-csv?limit=5000`
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = 'vector_documents.csv'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      toast({
        title: '다운로드 시작',
        description: 'CSV 파일을 다운로드 중입니다...',
        status: 'info',
        duration: 3000,
        isClosable: true,
      })
      
      // 다운로드 후 3초 뒤에 로딩 상태 해제
      setTimeout(() => {
        setIsDownloadingCSV(false)
      }, 3000)
      
    } catch (error) {
      console.error('CSV 다운로드 오류:', error)
      toast({
        title: '다운로드 실패',
        description: 'CSV 다운로드 중 오류가 발생했습니다.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      setIsDownloadingCSV(false)
    }
  }

  // 벡터 상태 배지 색상
  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'completed': return 'green'
      case 'pending': return 'yellow'
      case 'error': return 'red'
      default: return 'gray'
    }
  }

  // 벡터 상태 텍스트
  const getStatusText = (status?: string) => {
    switch (status) {
      case 'completed': return '완료'
      case 'pending': return '대기'
      case 'error': return '오류'
      default: return '알 수 없음'
    }
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* 페이지 헤더 */}
      <Box>
        <Heading size="lg" color="bexco.700" mb={2}>
          파일 관리
        </Heading>
        <Text color="gray.600">
          공간 정보, FAQ, 기타 문서를 업로드하여 RAG 시스템의 지식 베이스를 확장하세요.
        </Text>
      </Box>

      {/* 탭 */}
      <Tabs colorScheme="bexco">
        <TabList>
          {isAdmin && <Tab>파일 업로드</Tab>}
          <Tab>벡터화된 문서 청크</Tab>
          {isAdmin && <Tab>웹 크롤링</Tab>}
        </TabList>

        <TabPanels>
          {/* 파일 업로드 탭 */}
          {isAdmin && <TabPanel>
            <VStack spacing={4} align="stretch">
              {/* 업로드 버튼 - admin만 가능 */}
              {canUpload ? (
                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                  <CardBody>
                    <VStack spacing={4}>
                      <Button
                        leftIcon={<AddIcon />}
                        colorScheme="bexco"
                        onClick={() => fileInputRef.current?.click()}
                        isDisabled={isUploading}
                        size="lg"
                        width="full"
                      >
                        파일 선택
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.docx,.txt,.csv,.json,.md"
                        style={{ display: 'none' }}
                        onChange={handleFileUpload}
                      />
                      {isUploading && (
                        <Box width="full">
                          <Text mb={2}>업로드 중... {uploadProgress}%</Text>
                          <Progress value={uploadProgress} colorScheme="bexco" />
                        </Box>
                      )}
                      <Text fontSize="sm" color="gray.500">
                        지원 형식: .pdf, .docx, .txt, .csv, .json, .md (최대 10MB)
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>
              ) : (
                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                  <CardBody>
                    <Text color="gray.500" textAlign="center">
                      파일 업로드 권한이 없습니다. 관리자에게 문의하세요.
                    </Text>
                  </CardBody>
                </Card>
              )}

              {/* 업로드된 파일 목록 */}
              <Card bg={cardBg} border="1px" borderColor={borderColor}>
                <CardBody>
                  <Heading size="md" mb={4}>업로드된 파일</Heading>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>파일명</Th>
                          <Th>크기</Th>
                          <Th>업로드 날짜</Th>
                          <Th>상태</Th>
                          <Th>작업</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {files.length === 0 ? (
                          <Tr>
                            <Td colSpan={5} textAlign="center" py={8}>
                              <Text color="gray.500">업로드된 파일이 없습니다.</Text>
                            </Td>
                          </Tr>
                        ) : (
                          files.map((file) => (
                            <Tr key={file.filename}>
                              <Td>
                                <HStack>
                                  <AttachmentIcon />
                                  <Text>{file.originalName || file.filename}</Text>
                                </HStack>
                              </Td>
                              <Td>{(file.size / 1024).toFixed(2)} KB</Td>
                              <Td>{file.formattedDate || new Date(file.uploadDate).toLocaleString('ko-KR')}</Td>
                              <Td>
                                <Badge colorScheme={getStatusColor(file.vectorStatus)}>
                                  {getStatusText(file.vectorStatus)}
                                  {file.vectorCount ? ` (${file.vectorCount})` : ''}
                                </Badge>
                              </Td>
                              <Td>
                                <HStack spacing={2}>
                                  <IconButton
                                    aria-label="다운로드"
                                    icon={<DownloadIcon />}
                                    size="sm"
                                    variant="ghost"
                                    as="a"
                                    href={`/graph/files/${encodeURIComponent(file.filename)}${file.originalName ? `?originalName=${encodeURIComponent(file.originalName)}` : ''}`}
                                    download
                                  />
                                  <IconButton
                                    aria-label="삭제"
                                    icon={<DeleteIcon />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={() => handleFileDelete(file.filename)}
                                  />
                                </HStack>
                              </Td>
                            </Tr>
                          ))
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>
            </VStack>
          </TabPanel>}

          {/* 벡터화된 문서 청크 탭 */}
          <TabPanel>
            <VStack spacing={4} align="stretch">
              <Card bg={cardBg} border="1px" borderColor={borderColor}>
                <CardBody>
                  <HStack justify="space-between" mb={4}>
                    <Heading size="md">벡터화된 문서 청크 (총 {totalItems}개)</Heading>
                    <HStack spacing={2}>
                      {isAdmin && selectedChunks.size > 0 && (
                        <Button
                          size="sm"
                          colorScheme="red"
                          leftIcon={<DeleteIcon />}
                          onClick={handleMultipleDelete}
                          isLoading={isDeletingMultiple}
                        >
                          선택 삭제 ({selectedChunks.size})
                        </Button>
                      )}
                      {isAdmin && (
                        <Button
                          size="sm"
                          colorScheme="green"
                          leftIcon={<DownloadIcon />}
                          onClick={handleDownloadCSV}
                          isLoading={isDownloadingCSV}
                          loadingText="다운로드 중..."
                        >
                          📊 CSV 다운로드
                        </Button>
                      )}
                      {isAdmin && (
                        <Button
                          size="sm"
                          colorScheme="purple"
                          leftIcon={<DownloadIcon />}
                          onClick={handleDownloadProjector}
                          isLoading={isDownloadingProjector}
                          loadingText="다운로드 중..."
                        >
                          🎨 Projector
                        </Button>
                      )}
                      <Button
                        size="sm"
                        leftIcon={<RepeatIcon />}
                        onClick={() => fetchVectorDocuments(currentPage)}
                        isLoading={isLoadingVectors}
                      >
                        새로고침
                      </Button>
                    </HStack>
                  </HStack>

                  {isLoadingVectors ? (
                    <Box textAlign="center" py={8}>
                      <Spinner size="xl" color="bexco.500" />
                    </Box>
                  ) : vectorDocuments.length === 0 ? (
                    <Box textAlign="center" py={8}>
                      <Text color="gray.500">벡터화된 문서가 없습니다.</Text>
                    </Box>
                  ) : (
                    <>
                      <TableContainer>
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              {isAdmin && (
                                <Th width="40px">
                                  <Checkbox
                                    isChecked={vectorDocuments.length > 0 && selectedChunks.size === vectorDocuments.length}
                                    isIndeterminate={selectedChunks.size > 0 && selectedChunks.size < vectorDocuments.length}
                                    onChange={handleSelectAll}
                                  />
                                </Th>
                              )}
                              <Th>파일명</Th>
                              <Th>내용 미리보기</Th>
                              <Th>메타데이터</Th>
                              <Th>작업</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {vectorDocuments.length === 0 ? (
                              <Tr>
                                <Td colSpan={5} textAlign="center" py={8}>
                                  <Text color="gray.500">벡터화된 문서가 없습니다.</Text>
                                </Td>
                              </Tr>
                            ) : (
                              vectorDocuments.map((doc) => (
                                <Tr key={doc.id} bg={selectedChunks.has(doc.id) ? useColorModeValue('blue.50', 'blue.900') : 'transparent'}>
                                  {isAdmin && (
                                    <Td>
                                      <Checkbox
                                        isChecked={selectedChunks.has(doc.id)}
                                        onChange={() => handleCheckboxChange(doc.id)}
                                      />
                                    </Td>
                                  )}
                                  <Td>
                                    <Text 
                                      fontSize="sm" 
                                      fontWeight="medium"
                                      maxW="300px"
                                      overflow="hidden"
                                      textOverflow="ellipsis"
                                      whiteSpace="nowrap"
                                      title={doc.filename}
                                    >
                                      {doc.filename}
                                    </Text>
                                  </Td>
                                  <Td maxW="400px">
                                    <Text fontSize="sm" noOfLines={2}>
                                      {doc.content_preview}
                                    </Text>
                                  </Td>
                                  <Td>
                                    <Text fontSize="xs" color="gray.500">
                                      {JSON.stringify(doc.metadata).substring(0, 50)}...
                                    </Text>
                                  </Td>
                                  <Td>
                                    <HStack spacing={2}>
                                      <IconButton
                                        aria-label="상세보기"
                                        icon={<ViewIcon />}
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleViewChunk(doc)}
                                      />
                                      {isAdmin && (
                                        <IconButton
                                          aria-label="삭제"
                                          icon={<DeleteIcon />}
                                          size="sm"
                                          variant="ghost"
                                          colorScheme="red"
                                          onClick={() => handleChunkDelete(doc.id)}
                                        />
                                      )}
                                    </HStack>
                                  </Td>
                                </Tr>
                              ))
                            )}
                          </Tbody>
                        </Table>
                      </TableContainer>

                      {/* 페이지네이션 - 하단 */}
                      <HStack justify="space-between" p={4} flexWrap="wrap" spacing={4}>
                        {/* 🆕 v10.7: 페이지당 표시 개수 선택 */}
                        <HStack spacing={2}>
                          <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
                            페이지당:
                          </Text>
                          <Select
                            value={pageSize}
                            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                            size="sm"
                            maxW="80px"
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </Select>
                          <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
                            총 {totalItems}개
                          </Text>
                        </HStack>

                        {/* 페이지 버튼 */}
                        {totalPages > 1 && (
                        <HStack spacing={1}>
                          {/* 첫 페이지 버튼 */}
                          <Button
                            aria-label="첫 페이지"
                            size="sm"
                            variant="ghost"
                            isDisabled={currentPage === 1}
                            onClick={() => handlePageChange(1)}
                            minW="40px"
                          >
                            ««
                          </Button>
                          {/* 이전 페이지 버튼 */}
                          <IconButton
                            aria-label="이전 페이지"
                            icon={<ChevronLeftIcon />}
                            size="sm"
                            variant="ghost"
                            isDisabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                          />
                          {/* 페이지 번호 버튼들 */}
                          {getPageNumbers().map((page, index) => {
                            if (page === '...') {
                              return (
                                <Text key={`ellipsis-${index}`} fontSize="sm" px={2} color="gray.500">
                                  ...
                                </Text>
                              )
                            }
                            const pageNum = page as number
                            const isActive = pageNum === currentPage
                            return isActive ? (
                              <Button
                                key={pageNum}
                                size="sm"
                                variant="outline"
                                colorScheme="blue"
                                onClick={() => handlePageChange(pageNum)}
                                minW="40px"
                                fontWeight="bold"
                                cursor="default"
                              >
                                {pageNum}
                              </Button>
                            ) : (
                              <Button
                                key={pageNum}
                                size="sm"
                                variant="ghost"
                                onClick={() => handlePageChange(pageNum)}
                                minW="40px"
                                color="gray.600"
                                _hover={{ bg: 'gray.100' }}
                              >
                                {pageNum}
                              </Button>
                            )
                          })}
                          {/* 다음 페이지 버튼 */}
                          <IconButton
                            aria-label="다음 페이지"
                            icon={<ChevronRightIcon />}
                            size="sm"
                            variant="ghost"
                            isDisabled={currentPage === totalPages}
                            onClick={() => handlePageChange(currentPage + 1)}
                          />
                          {/* 마지막 페이지 버튼 */}
                          <Button
                            aria-label="마지막 페이지"
                            size="sm"
                            variant="ghost"
                            isDisabled={currentPage === totalPages}
                            onClick={() => handlePageChange(totalPages)}
                            minW="40px"
                          >
                            »»
                          </Button>
                        </HStack>
                        )}
                      </HStack>
                    </>
                  )}
                </CardBody>
              </Card>
            </VStack>
          </TabPanel>

          {/* 웹 크롤링 탭 - admin 사용자만 표시 */}
          {isAdmin && (
            <TabPanel>
              <VStack spacing={4} align="stretch">
                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                  <CardBody>
                    <Heading size="md" mb={4}>웹 크롤링</Heading>
                  <VStack spacing={4}>
                    <HStack width="full">
                      <Input
                        placeholder="https://example.com 또는 https://bexco.co.kr/board"
                        value={crawlUrl}
                        onChange={(e) => setCrawlUrl(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleWebCrawl()}
                      />
                      <Button
                        leftIcon={<ExternalLinkIcon />}
                        colorScheme="bexco"
                        onClick={handleWebCrawl}
                        isLoading={isCrawling}
                        loadingText="크롤링 중..."
                      >
                        페이지 크롤링 및 벡터화
                      </Button>
                    </HStack>
                    <Input
                      placeholder="CSS Selector (선택사항): #bdListWrap, .bdViewCont"
                      value={crawlSelector}
                      onChange={(e) => setCrawlSelector(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleWebCrawl()}
                      size="sm"
                    />
                    <Text fontSize="sm" color="gray.500">
                      단일 웹페이지의 내용을 크롤링하여 자동으로 벡터화합니다.<br />
                      <strong>CSS Selector</strong>를 지정하면 특정 영역만 추출합니다. (예: <code>#bdListWrap</code>, <code>.bdViewCont</code>)
                    </Text>
                  </VStack>
                </CardBody>
              </Card>

              {/* 크롤링 결과 */}
              {crawlResults.length > 0 && (
                <Card bg={cardBg} border="1px" borderColor={borderColor}>
                  <CardBody>
                    <Heading size="md" mb={4}>크롤링 결과</Heading>
                    <TableContainer>
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>URL</Th>
                            <Th>제목</Th>
                            <Th>청크 수</Th>
                            <Th>내용 길이</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {crawlResults.map((result, index) => (
                            <Tr key={index}>
                              <Td>
                                <Text fontSize="sm" noOfLines={1}>
                                  {result.url}
                                </Text>
                              </Td>
                              <Td>{result.title}</Td>
                              <Td>{result.chunks_count}개</Td>
                              <Td>{result.content_length.toLocaleString()}자</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </CardBody>
                </Card>
              )}
            </VStack>
          </TabPanel>
          )}
        </TabPanels>
      </Tabs>

      {/* 청크 상세보기 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>청크 상세 정보</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedDocument && (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="bold" mb={2}>ID:</Text>
                  <Text fontSize="sm" color="gray.600">{selectedDocument.id}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>파일명:</Text>
                  <Text fontSize="sm">{selectedDocument.filename}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>내용:</Text>
                  <Box
                    p={4}
                    bg="gray.50"
                    borderRadius="md"
                    maxH="400px"
                    overflowY="auto"
                  >
                    <Text fontSize="sm" whiteSpace="pre-wrap">
                      {selectedDocument.full_content}
                    </Text>
                  </Box>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>메타데이터:</Text>
                  <Box
                    p={4}
                    bg="gray.50"
                    borderRadius="md"
                    maxH="200px"
                    overflowY="auto"
                  >
                    <Text fontSize="sm" fontFamily="mono">
                      {JSON.stringify(selectedDocument.metadata, null, 2)}
                    </Text>
                  </Box>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>닫기</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 다중 삭제 확인 다이얼로그 */}
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              청크 다중 삭제
            </AlertDialogHeader>
            <AlertDialogBody>
              선택한 <strong>{selectedChunks.size}개</strong>의 청크를 삭제하시겠습니까?
              <br />
              이 작업은 되돌릴 수 없습니다.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteDialogClose}>
                취소
              </Button>
              <Button colorScheme="red" onClick={confirmMultipleDelete} ml={3}>
                삭제
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  )
}

export default FileManagementPage
